import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Input, Row, Tooltip, Typography, theme } from 'antd';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import axios from 'axios';
import API from 'constants/api';
import SITELINKS from 'constants/sitelinks';
import { loginSchema, recoverSchema, confirmSchema } from './schemas';
import './styles.css';

const { Text } = Typography;

// ponytail: textos en español fijos. El lib traía i18n pero el selector de
// idioma nunca se mostraba, así que resolvía por navegador y quedaba en inglés.
const TITLE = 'Operaciones';

const LoginField = ({ name, label, type }) => (
  <div className="loginFieldGroup">
    <Text className="loginFieldLabel">{label}</Text>
    <Field
      as={type === 'password' ? Input.Password : Input}
      name={name}
      type={type}
      placeholder={label}
      size="large"
    />
    <ErrorMessage name={name}>
      {(msg) => (
        <Text type="danger" className="loginLabel">
          {msg}
        </Text>
      )}
    </ErrorMessage>
  </div>
);

LoginField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

const SubmitButton = ({ loading, children }) => (
  <Button
    loading={loading}
    htmlType="submit"
    type="primary"
    className="loginSubmit"
  >
    {children}
  </Button>
);

SubmitButton.propTypes = {
  loading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

const Credentials = ({ onSubmit, isLoggingIn }) => (
  // El Tooltip va afuera del Form: adentro es flujo inline y queda al lado del
  // botón. Afuera cae como item del flex column de .loginBox y apila.
  <>
    <Formik
      initialValues={{ email: '', password: '' }}
      validationSchema={loginSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <LoginField type="email" name="email" label="Email" />
        <LoginField type="password" name="password" label="Contraseña" />
        <SubmitButton loading={isLoggingIn}>Ingresar</SubmitButton>
      </Form>
    </Formik>
    <Tooltip title="Pedile ayuda a tu administrador para restablecer tu contraseña.">
      <Text className="loginLabel">¿Olvidaste tu contraseña?</Text>
    </Tooltip>
  </>
);

Credentials.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoggingIn: PropTypes.bool.isRequired,
};

const RecoverPassword = () => {
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async ({ email }) => {
    setLoading(true);
    try {
      await axios.post(`${API.auth.recover}${email}`);
      history.push(SITELINKS.confirm);
    } catch (ex) {
      setError('No encontramos ese email.');
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{ email: '' }}
      validationSchema={recoverSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <Text className="loginLabel">
          Te enviamos un código para restablecer tu contraseña.
        </Text>
        <LoginField type="email" name="email" label="Email" />
        {error && (
          <Text type="danger" className="loginLabel">
            {error}
          </Text>
        )}
        <SubmitButton loading={isLoading}>Enviar</SubmitButton>
      </Form>
    </Formik>
  );
};

const ConfirmRecovery = () => {
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async ({ code, newPassword }) => {
    setLoading(true);
    try {
      await axios.post(API.auth.confirm, { code, NewPassword: newPassword });
      history.push(SITELINKS.login);
    } catch (ex) {
      setError('El código de verificación no es válido.');
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{ code: '', newPassword: '', confirmPassword: '' }}
      validationSchema={confirmSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <Text className="loginLabel">
          Ingresá el código que te enviamos por email y tu nueva contraseña.
        </Text>
        <LoginField type="text" name="code" label="Código de verificación" />
        <LoginField
          type="password"
          name="newPassword"
          label="Nueva contraseña"
        />
        <LoginField
          type="password"
          name="confirmPassword"
          label="Confirmar contraseña"
        />
        {error && (
          <Text type="danger" className="loginLabel">
            {error}
          </Text>
        )}
        <SubmitButton loading={isLoading}>Confirmar</SubmitButton>
      </Form>
    </Formik>
  );
};

const LoginForm = ({ onSubmit, isLoggingIn }) => {
  const { token } = theme.useToken();
  return (
    <div
      className="loginLayout"
      style={{ background: token.colorBgBase || '#ffffff' }}
    >
      <Row justify="center" align="middle" className="loginContent">
        <Col className="loginBox" xs={22} md={16} lg={12} xl={10} xxl={6}>
          <Text className="loginTitle">{TITLE}</Text>
          <Row justify="center">
            <Col className="loginBox" lg={{ span: 14 }} xs={{ span: 18 }}>
              <Switch>
                <Route
                  path={SITELINKS.recover}
                  component={RecoverPassword}
                  exact
                />
                <Route
                  path={SITELINKS.confirm}
                  component={ConfirmRecovery}
                  exact
                />
                <Route
                  render={() => (
                    <Credentials
                      onSubmit={onSubmit}
                      isLoggingIn={isLoggingIn}
                    />
                  )}
                />
              </Switch>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoggingIn: PropTypes.bool.isRequired,
};

export default LoginForm;
